// PackingMaterialSettings — Manager-only settings tab for packing material
// thresholds, reorder quantities, and auto-transfer configuration (WCO-2155).

import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Plus, Package, Settings, AlertTriangle, Trash2, Search, X } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import productionService from '../../services/productionService';
// Shared with Inventory → Packing Materials so the two views can't disagree on "low".
import { getPackingStockStatus, packingStockStatusColors } from '../../utils/packingStockStatus';
import { PackingQuantity, UnitsHint } from '../../components/PackingQuantity';
import { usePackingStockLive } from '../../hooks/usePackingStockLive';

interface ThresholdRow {
  id: number;
  item_number: string;
  item_name: string | null;
  packing: string | null;
  item_unit: string | null;
  item_sub_unit: string | null;
  item_sub_quantity: number | null;
  safe_quantity: number;
  reorder_quantity: number;
  /** Pieces per bundle x safe_quantity, resolved server-side. Null = unresolvable. */
  safe_pieces: number | null;
  /** Pieces per bundle x reorder_quantity, resolved server-side. Null = unresolvable. */
  reorder_pieces: number | null;
  pieces_per_bundle: number | null;
  current_stock: number;
  /** On-hand expressed in bundles/cases (FLOOR), computed server-side. Null = unresolvable. */
  current_stock_bundles: number | null;
  current_stock_remainder: number | null;
}


interface EditState {
  [itemNumber: string]: {
    safe_quantity: string;
    reorder_quantity: string;
  };
}

const PackingMaterialSettings: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Live on-hand. This page previously had NO inbound WebSocket listener at all, so
  // Current Stock stayed stale until a manual reload.
  usePackingStockLive();

  // Auto-transfer settings from config
  const { data: configSettings = [] } = useQuery(
    'systemSettings-config',
    () => productionService.getConfigSettings(),
    { staleTime: 30_000 }
  );

  const autoTransferMode = configSettings.find(s => s.setting_key === 'auto_transfer_mode')?.setting_value || 'draft';
  const autoTransferSourceLocation = configSettings.find(s => s.setting_key === 'auto_transfer_source_location')?.setting_value || 'X';
  const autoTransferWhen = configSettings.find(s => s.setting_key === 'auto_transfer_when')?.setting_value || 'same_day';

  // Only thresholds that have been configured (not all packing materials)
  const { data: thresholds = [], isLoading } = useQuery<ThresholdRow[]>(
    'packing-thresholds',
    () => productionService.getPackingThresholds(),
    { staleTime: 30_000 }
  );

  // Editable state for each row
  const [edits, setEdits] = useState<EditState>({});

  // Add material modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [allMaterials, setAllMaterials] = useState<Array<{ item_number: string; item_name: string }>>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize edit state when thresholds load
  React.useEffect(() => {
    if (thresholds.length > 0) {
      const initial: EditState = {};
      thresholds.forEach(t => {
        if (!edits[t.item_number]) {
          initial[t.item_number] = {
            safe_quantity: String(t.safe_quantity),
            reorder_quantity: String(t.reorder_quantity),
          };
        }
      });
      setEdits(prev => ({ ...initial, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds]);

  const handleEditChange = (itemNumber: string, field: 'safe_quantity' | 'reorder_quantity', value: string) => {
    setEdits(prev => ({
      ...prev,
      [itemNumber]: { ...prev[itemNumber], [field]: value }
    }));
  };

  // Auto-save on Enter
  const saveThresholdMutation = useMutation(
    ({ itemNumber, safe_quantity, reorder_quantity }: { itemNumber: string; safe_quantity: number; reorder_quantity: number }) =>
      productionService.updatePackingThreshold(itemNumber, safe_quantity, reorder_quantity),
    {
      onSuccess: () => {
        toast.success(t('admin.packingMaterials.thresholdSaved'));
        queryClient.invalidateQueries('packing-thresholds');
      },
      onError: () => {
        toast.error(t('admin.packingMaterials.thresholdSaveFailed'));
      }
    }
  );

  const handleBlurSave = (itemNumber: string) => {
    const edit = edits[itemNumber];
    if (!edit) return;
    const row = thresholds.find(t => t.item_number === itemNumber);
    const safeQty = parseFloat(edit.safe_quantity) || 0;
    const reorderQty = parseFloat(edit.reorder_quantity) || 0;
    // Only save if values actually changed
    if (row && safeQty === Number(row.safe_quantity) && reorderQty === Number(row.reorder_quantity)) return;
    saveThresholdMutation.mutate({ itemNumber, safe_quantity: safeQty, reorder_quantity: reorderQty });
  };

  // Delete threshold
  const deleteThresholdMutation = useMutation(
    (itemNumber: string) => productionService.deletePackingThreshold(itemNumber),
    {
      onSuccess: () => {
        toast.success(t('admin.packingMaterials.thresholdDeleted'));
        queryClient.invalidateQueries('packing-thresholds');
        setConfirmDeleteItem(null);
      },
      onError: () => {
        toast.error(t('admin.packingMaterials.thresholdSaveFailed'));
      }
    }
  );

  // Auto-transfer mode toggle
  const saveModeMutation = useMutation(
    (mode: string) => productionService.updateConfigSetting('auto_transfer_mode', mode),
    {
      onSuccess: () => {
        toast.success(t('admin.packingMaterials.settingSaved'));
        queryClient.invalidateQueries('systemSettings-config');
      }
    }
  );

  // Source location update
  const saveLocationMutation = useMutation(
    (location: string) => productionService.updateConfigSetting('auto_transfer_source_location', location),
    {
      onSuccess: () => {
        toast.success(t('admin.packingMaterials.settingSaved'));
        queryClient.invalidateQueries('systemSettings-config');
      }
    }
  );

  // When the auto-generated transfer is scheduled for (WCO-2155)
  const saveWhenMutation = useMutation(
    (when: string) => productionService.updateConfigSetting('auto_transfer_when', when),
    {
      onSuccess: () => {
        toast.success(t('admin.packingMaterials.settingSaved'));
        queryClient.invalidateQueries('systemSettings-config');
      }
    }
  );

  // Open add modal — fetch all packing materials for picker
  const handleOpenAddModal = async () => {
    setShowAddModal(true);
    setMaterialSearch('');
    setLoadingMaterials(true);
    try {
      const data = await productionService.getPackingMaterials();
      setAllMaterials(data);
    } catch {
      toast.error('Failed to load packing materials');
    } finally {
      setLoadingMaterials(false);
    }
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  // Add a material as a threshold (with defaults)
  const handleAddMaterial = (itemNumber: string) => {
    saveThresholdMutation.mutate(
      { itemNumber, safe_quantity: 0, reorder_quantity: 0 },
      {
        onSuccess: () => {
          setEdits(prev => ({
            ...prev,
            [itemNumber]: { safe_quantity: '0', reorder_quantity: '0' }
          }));
        }
      }
    );
    setShowAddModal(false);
  };

  // Filter materials in modal: exclude already-configured and match search
  const filteredAddMaterials = React.useMemo(() => {
    const existingNumbers = new Set(thresholds.map(t => t.item_number));
    let filtered = allMaterials.filter(m => !existingNumbers.has(m.item_number));
    if (materialSearch.trim().length >= 2) {
      const q = materialSearch.toLowerCase();
      filtered = filtered.filter(m =>
        m.item_number.toLowerCase().includes(q) ||
        (m.item_name || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [allMaterials, thresholds, materialSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Global auto-transfer settings — single row */}
      <div className="card px-6 py-3">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-secondary-500" />
            <span className="text-sm font-medium text-secondary-900 dark:text-white">
              {t('admin.packingMaterials.autoTransferSettings')}
            </span>
          </div>

          {/* Auto-transfer mode toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-500 dark:text-gray-400">
              {t('admin.packingMaterials.autoTransferMode')}:
            </span>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={autoTransferMode === 'auto_submit'}
                onChange={(e) => saveModeMutation.mutate(e.target.checked ? 'auto_submit' : 'draft')}
              />
              <div className="w-9 h-5 bg-secondary-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
            <span className="text-xs font-medium text-secondary-600 dark:text-gray-300">
              {autoTransferMode === 'draft' ? t('admin.packingMaterials.modeDraft') : t('admin.packingMaterials.modeAutoSubmit')}
            </span>
          </div>

          {/* Source warehouse location */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-500 dark:text-gray-400">
              {t('admin.packingMaterials.sourceLocation')}:
            </span>
            <select
              value={autoTransferSourceLocation}
              onChange={(e) => saveLocationMutation.mutate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="X">X - Glendale Sales</option>
              <option value="G">G - Glendale</option>
              <option value="M">M - Maspeth</option>
              <option value="C">C - Chinatown</option>
            </select>
          </div>

          {/* When the auto-generated transfer is scheduled for */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-500 dark:text-gray-400">
              {t('admin.packingMaterials.autoTransferWhen')}:
            </span>
            <select
              value={autoTransferWhen}
              onChange={(e) => saveWhenMutation.mutate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="same_day">{t('admin.packingMaterials.whenSameDay')}</option>
              <option value="next_business_day">{t('admin.packingMaterials.whenNextBusinessDay')}</option>
              <option value="next_monday">{t('admin.packingMaterials.whenNextMonday')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Per-material thresholds */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-secondary-500" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
              {t('admin.packingMaterials.thresholds')}
            </h3>
          </div>
          <button
            onClick={handleOpenAddModal}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('common.add')}
          </button>
        </div>

        {thresholds.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
            <Package className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">{t('admin.packingMaterials.noThresholds')}</p>
            <p className="text-xs mt-1">{t('admin.packingMaterials.addThresholdHint')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.packingMaterials.itemNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.packingMaterials.itemName')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.packingMaterials.currentStock')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.packingMaterials.safeQuantity')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.packingMaterials.reorderQuantity')}
                  </th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-gray-700">
                {thresholds.map((row) => {
                  const edit = edits[row.item_number];
                  const status = getPackingStockStatus(row);
                  return (
                    <tr key={row.item_number} className="hover:bg-secondary-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3 text-sm font-mono text-secondary-900 dark:text-white">
                        {row.item_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-700 dark:text-gray-300">
                        {row.item_name || '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${packingStockStatusColors[status]}`}>
                        <div className="flex items-center justify-end space-x-1">
                          {(status === 'low' || status === 'critical') && <AlertTriangle className="w-3.5 h-3.5" />}
                          {/* Bundles/cases, matching the Safe/Reorder inputs below; exact
                              piece count is in the ⓘ tooltip. Conversion is server-side. */}
                          <PackingQuantity
                            bundles={row.current_stock_bundles}
                            pieces={row.current_stock}
                            unit={row.item_unit}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <input
                            type="number"
                            min="0"
                            value={edit?.safe_quantity ?? ''}
                            onChange={(e) => handleEditChange(row.item_number, 'safe_quantity', e.target.value)}
                            onBlur={() => handleBlurSave(row.item_number)}
                            className="w-20 px-2 py-1.5 text-right text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0"
                          />
                          <span
                            className="text-xs font-medium text-secondary-400 dark:text-gray-500 whitespace-nowrap cursor-help"
                            title={row.pieces_per_bundle && row.item_unit ? `${row.pieces_per_bundle} ${row.item_sub_unit || 'PC'} per ${row.item_unit}` : ''}
                          >
                            {row.item_unit || ''}
                          </span>
                          {/* Piece equivalent of the value in the input (server-computed). */}
                          <UnitsHint pieces={row.safe_pieces} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <input
                            type="number"
                            min="0"
                            value={edit?.reorder_quantity ?? ''}
                            onChange={(e) => handleEditChange(row.item_number, 'reorder_quantity', e.target.value)}
                            onBlur={() => handleBlurSave(row.item_number)}
                            className="w-20 px-2 py-1.5 text-right text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0"
                          />
                          <span
                            className="text-xs font-medium text-secondary-400 dark:text-gray-500 whitespace-nowrap cursor-help"
                            title={row.pieces_per_bundle && row.item_unit ? `${row.pieces_per_bundle} ${row.item_sub_unit || 'PC'} per ${row.item_unit}` : ''}
                          >
                            {row.item_unit || ''}
                          </span>
                          {/* Piece equivalent of the value in the input (server-computed). */}
                          <UnitsHint pieces={row.reorder_pieces} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {confirmDeleteItem === row.item_number ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteThresholdMutation.mutate(row.item_number)}
                              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                            >
                              {t('common.yes')}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteItem(null)}
                              className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded transition-colors"
                            >
                              {t('common.no')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteItem(row.item_number)}
                            className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add material modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.packingMaterials.addMaterial')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  ref={searchInputRef}
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  placeholder={t('packingMaterialMapping.searchItems')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                {loadingMaterials ? (
                  <div className="p-3 text-center text-sm text-gray-500">Loading...</div>
                ) : filteredAddMaterials.length === 0 ? (
                  <div className="p-3 text-center text-sm text-gray-500">
                    {materialSearch.length >= 2 ? t('packingMaterialMapping.noResults') : t('admin.packingMaterials.typeToSearch')}
                  </div>
                ) : (
                  filteredAddMaterials.map((material) => (
                    <button
                      key={material.item_number}
                      onClick={() => handleAddMaterial(material.item_number)}
                      className="w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {material.item_name || material.item_number}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {material.item_number}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingMaterialSettings;
